/**
 * Error serialization for crash reporting.
 *
 * `window.onunhandledrejection` can deliver ANY value as the rejection reason —
 * plain objects (IPC results, JSON-RPC error responses), strings, numbers.
 * Passing those through `new Error(String(reason))` yields the useless
 * "[object Object]" message AND a stack whose top frame is the synthesis site
 * inside the crash-dialog code, which the location extractor then reports as
 * the crash location (e.g. "at [GlobalErrorListener.tsx:76:40]").
 *
 * `toReportableError` converts an arbitrary rejection value into an Error whose
 * message is diagnosable and whose stack is either the value's own `.stack`
 * (when it carries one) or empty — never the synthesized frame.
 */

const MAX_JSON_LENGTH = 500;

/** JSON.stringify with cycle guard, bigint support, and a length cap. */
function safeJsonStringify(value: unknown): string | null {
  try {
    const seen = new WeakSet<object>();
    const json = JSON.stringify(value, (_key, v) => {
      if (typeof v === 'bigint') return `${v}n`;
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]';
        seen.add(v);
      }
      return v;
    });
    // '{}' / 'null' / undefined carry no information — let the caller fall
    // back to String(value) instead.
    if (!json || json === '{}' || json === 'null') return null;
    return json.length > MAX_JSON_LENGTH ? `${json.slice(0, MAX_JSON_LENGTH)}…` : json;
  } catch {
    return null;
  }
}

/** Build a human-useful message from an error-like or arbitrary object. */
function describeObject(value: object): string {
  const rec = value as Record<string, unknown>;
  const message = typeof rec.message === 'string' && rec.message ? rec.message : null;
  if (message) {
    const name = typeof rec.name === 'string' && rec.name && rec.name !== 'Error' ? rec.name : null;
    const code =
      typeof rec.code === 'string' || typeof rec.code === 'number' ? String(rec.code) : null;
    const prefix = name ? `${name}: ` : '';
    const suffix = code && !message.includes(code) ? ` (code ${code})` : '';
    return `${prefix}${message}${suffix}`;
  }
  return safeJsonStringify(value) ?? String(value);
}

/**
 * Create an Error for a message synthesized from an event payload. The stack
 * is the supplied origin stack or EMPTY — never this callsite — so the crash
 * location extractor can't report crash-dialog code as the crash location.
 */
export function syntheticError(message: string, originStack?: string): Error {
  const err = new Error(message);
  err.stack = originStack ?? '';
  return err;
}

/**
 * Convert an arbitrary thrown/rejected value into an Error suitable for crash
 * context collection. Error instances pass through untouched.
 */
export function toReportableError(value: unknown): Error {
  if (value instanceof Error) return value;

  if (typeof value === 'object' && value !== null) {
    const rec = value as Record<string, unknown>;
    const originStack = typeof rec.stack === 'string' && rec.stack ? rec.stack : undefined;
    return syntheticError(describeObject(value), originStack);
  }

  return syntheticError(String(value));
}
